import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StatsCard from './components/StatsCard';
import JobPostingCard from './components/JobPostingCard';
import ActivityFeed from './components/ActivityFeed';
import QuickActions from './components/QuickActions';
import PerformanceChart from './components/PerformanceChart';
import SubscriptionCard from './components/SubscriptionCard';
import TopPerformingJobs from './components/TopPerformingJobs';
import { jobService } from '../../services/jobService';

const EmployerDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [jobPostings, setJobPostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalApplications: 0,
    activeJobs: 0,
    totalJobs: 0
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    loadJobs();
    loadStats();
  }, []);

  const loadJobs = async () => {
    try {
      setLoading(true);
      setError('');
      const jobs = await jobService?.getEmployerJobs();
      
      // Transform data to match component expectations
      const transformedJobs = jobs?.map((job) => ({
        id: job?.id,
        title: job?.jobTitle,
        location: `${job?.city || ''}, ${job?.state || ''}`?.trim(),
        type: job?.employmentType,
        salary: job?.salaryMin && job?.salaryMax 
          ? `₹${(job?.salaryMin / 100000)?.toFixed(2)}L - ₹${(job?.salaryMax / 100000)?.toFixed(2)}L`
          : 'Not specified',
        status: job?.status?.charAt(0)?.toUpperCase() + job?.status?.slice(1),
        applications: job?.applicationsCount || 0,
        views: job?.viewsCount || 0,
        postedDate: new Date(job?.postedDate)?.toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }),
        expiryDate: job?.expiryDate 
          ? new Date(job?.expiryDate)?.toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            })
          : 'No expiry'
      })) || [];
      
      setJobPostings(transformedJobs);
    } catch (err) {
      console.error('Error loading jobs:', err);
      setError(err?.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const jobStats = await jobService?.getJobStats();
      setStats({
        totalApplications: jobStats?.totalApplications || 0,
        activeJobs: jobStats?.activeJobs || 0,
        totalJobs: jobStats?.totalJobs || 0
      });
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const statsData = [
    {
      icon: "FileText",
      title: "Total Applications",
      value: stats?.totalApplications?.toString(),
      change: "+12.5%",
      changeType: "positive",
      iconBgColor: "bg-primary/10",
      iconColor: "var(--color-primary)"
    },
    {
      icon: "Briefcase",
      title: "Active Job Posts",
      value: stats?.activeJobs?.toString(),
      change: "+3",
      changeType: "positive",
      iconBgColor: "bg-success/10",
      iconColor: "var(--color-success)"
    },
    {
      icon: "Star",
      title: "Shortlisted",
      value: "89",
      change: "+8.2%",
      changeType: "positive",
      iconBgColor: "bg-warning/10",
      iconColor: "var(--color-warning)"
    },
    {
      icon: "UserCheck",
      title: "Hired This Month",
      value: "12",
      change: "-2",
      changeType: "negative",
      iconBgColor: "bg-accent/10",
      iconColor: "var(--color-accent)"
    }
  ];

  const recentActivities = [
    {
      id: 1,
      type: "application",
      message: "Rahul Kumar applied for Senior React Developer position",
      timestamp: new Date(Date.now() - 900000)
    },
    {
      id: 2,
      type: "shortlist",
      message: "Priya Sharma shortlisted for Product Manager interview",
      timestamp: new Date(Date.now() - 1800000)
    },
    {
      id: 3,
      type: "application",
      message: "Amit Patel applied for UI/UX Designer position",
      timestamp: new Date(Date.now() - 3600000)
    },
    {
      id: 4,
      type: "interview",
      message: "Interview scheduled with Sneha Reddy for DevOps Engineer",
      timestamp: new Date(Date.now() - 7200000)
    },
    {
      id: 5,
      type: "message",
      message: "New message from Vikram Singh regarding Senior React Developer role",
      timestamp: new Date(Date.now() - 10800000)
    }
  ];

  const performanceData = [
    { month: "Jun", applications: 145, views: 2100 },
    { month: "Jul", applications: 189, views: 2650 },
    { month: "Aug", applications: 234, views: 3200 },
    { month: "Sep", applications: 198, views: 2890 },
    { month: "Oct", applications: 267, views: 3450 },
    { month: "Nov", applications: 214, views: 2980 }
  ];

  const subscriptionData = {
    plan: "Premium",
    jobPostsRemaining: 17,
    jobPostsLimit: 50,
    featuredPosts: 3,
    featuredPostsLimit: 10,
    renewalDate: "15 Dec 2025",
    features: [
      "Unlimited candidate database access",
      "Advanced analytics dashboard",
      "Priority customer support",
      "Featured job postings",
      "Resume database search"
    ]
  };

  const topPerformingJobs = jobPostings
    ?.slice(0, 3)
    ?.map((job) => ({
      id: job?.id,
      title: job?.title,
      applications: job?.applications,
      views: job?.views,
      conversionRate: job?.views > 0 ? ((job?.applications / job?.views) * 100)?.toFixed(1) : 0
    })) || [];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Employer Dashboard</h1>
                <p className="text-muted-foreground">Manage your recruitment activities and track hiring performance</p>
              </div>
              <Button
                variant="default"
                iconName="PlusCircle"
                iconPosition="left"
                onClick={() => navigate('/post-new-job')}
              >
                Post New Job
              </Button>
            </div>

            <div className="flex items-center gap-2 border-b border-border overflow-x-auto">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-4 py-3 text-sm font-medium transition-smooth whitespace-nowrap ${
                  activeTab === 'overview' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('jobs')}
                className={`px-4 py-3 text-sm font-medium transition-smooth whitespace-nowrap ${
                  activeTab === 'jobs' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                Job Posts
              </button>
              <button
                onClick={() => setActiveTab('analytics')}
                className={`px-4 py-3 text-sm font-medium transition-smooth whitespace-nowrap ${
                  activeTab === 'analytics' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                Analytics
              </button>
            </div>
          </div>

          {/* Loading State */}
          {loading && activeTab === 'jobs' && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Loading jobs...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && activeTab === 'jobs' && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 mb-8">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={24} color="var(--color-destructive)" />
                <div>
                  <h3 className="text-lg font-semibold text-destructive mb-1">Error Loading Jobs</h3>
                  <p className="text-sm text-muted-foreground">{error}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="RefreshCw"
                    iconPosition="left"
                    onClick={loadJobs}
                    className="mt-3"
                  >
                    Retry
                  </Button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'overview' && !loading && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {statsData?.map((stat, index) => (
                  <StatsCard key={index} {...stat} />
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-semibold text-foreground">Quick Actions</h2>
                    </div>
                    <QuickActions />
                  </div>

                  <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-semibold text-foreground">Recent Job Posts</h2>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="ArrowRight"
                        iconPosition="right"
                        onClick={() => setActiveTab('jobs')}
                      >
                        View All
                      </Button>
                    </div>
                    {jobPostings?.length === 0 ? (
                      <div className="text-center py-8">
                        <Icon name="Briefcase" size={48} className="text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground mb-4">No jobs posted yet</p>
                        <Button
                          variant="default"
                          size="sm"
                          iconName="PlusCircle"
                          iconPosition="left"
                          onClick={() => navigate('/post-new-job')}
                        >
                          Post Your First Job
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {jobPostings?.slice(0, 2)?.map((job) => (
                          <JobPostingCard key={job?.id} job={job} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl font-semibold text-foreground">Recent Activity</h2>
                      <Icon name="Bell" size={20} className="text-muted-foreground" />
                    </div>
                    <ActivityFeed activities={recentActivities} />
                  </div>

                  <SubscriptionCard subscription={subscriptionData} />
                </div>
              </div>
            </>
          )}

          {activeTab === 'jobs' && !loading && !error && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-3">
                  <Icon name="Briefcase" size={24} color="var(--color-primary)" />
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">All Job Postings</h2>
                    <p className="text-sm text-muted-foreground">{jobPostings?.length} total jobs</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" iconName="Filter" iconPosition="left">
                    Filter
                  </Button>
                  <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
                    Export
                  </Button>
                </div>
              </div>

              {jobPostings?.length === 0 ? (
                <div className="bg-card border border-border rounded-lg p-12 text-center">
                  <Icon name="Briefcase" size={64} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-foreground mb-2">No Jobs Posted Yet</h3>
                  <p className="text-muted-foreground mb-6">Start by posting your first job opening</p>
                  <Button
                    variant="default"
                    iconName="PlusCircle"
                    iconPosition="left"
                    onClick={() => navigate('/post-new-job')}
                  >
                    Post New Job
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-6">
                  {jobPostings?.map((job) => (
                    <JobPostingCard key={job?.id} job={job} />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-1">Performance Analytics</h2>
                    <p className="text-sm text-muted-foreground">Track your recruitment metrics over time</p>
                  </div>
                  <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
                    Export Report
                  </Button>
                </div>
                <PerformanceChart data={performanceData} />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-foreground">Top Performing Jobs</h2>
                    <Icon name="TrendingUp" size={20} className="text-success" />
                  </div>
                  {topPerformingJobs?.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No job data available</p>
                  ) : (
                    <TopPerformingJobs jobs={topPerformingJobs} />
                  )}
                </div>

                <div className="bg-card border border-border rounded-lg p-6">
                  <h2 className="text-xl font-semibold text-foreground mb-6">Hiring Funnel</h2>
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Applications Received</span>
                        <span className="text-sm font-semibold text-foreground">{stats?.totalApplications}</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-3">
                        <div className="bg-primary h-3 rounded-full" style={{ width: '100%' }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Screened</span>
                        <span className="text-sm font-semibold text-foreground">456</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-3">
                        <div className="bg-primary h-3 rounded-full" style={{ width: '36.6%' }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Interviewed</span>
                        <span className="text-sm font-semibold text-foreground">189</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-3">
                        <div className="bg-primary h-3 rounded-full" style={{ width: '15.2%' }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Offered</span>
                        <span className="text-sm font-semibold text-foreground">45</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-3">
                        <div className="bg-success h-3 rounded-full" style={{ width: '3.6%' }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Hired</span>
                        <span className="text-sm font-semibold text-foreground">12</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-3">
                        <div className="bg-success h-3 rounded-full" style={{ width: '1%' }} />
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 p-4 bg-success/10 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="TrendingUp" size={18} color="var(--color-success)" />
                      <span className="text-sm font-semibold text-success">Conversion Rate: 0.96%</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Your hiring conversion rate is above industry average</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default EmployerDashboard;